/**
 * Model data: fans, messages, purchases, connection history.
 * Filtering by model_id via query (backend).
 */
import { apiJson } from './client.js';

export async function getFans({ modelId, isTest, search, limit = 50, offset = 0 } = {}) {
  const params = new URLSearchParams({ limit, offset })
  if (modelId != null) params.set('model_id', modelId)
  if (isTest != null) params.set('is_test', String(isTest))
  if (search) params.set('search', search)
  return apiJson(`/admin/fans?${params}`, {}, 'Failed to load fans')
}

export async function getMessages({ modelId, limit = 50, offset = 0 } = {}) {
  const params = new URLSearchParams({ limit, offset })
  if (modelId != null) params.set('model_id', modelId)
  return apiJson(`/admin/messages?${params}`, {}, 'Failed to load messages')
}

export async function getPurchases({ modelId, isTest, limit = 50, offset = 0 } = {}) {
  const params = new URLSearchParams({ limit, offset })
  if (modelId != null) params.set('model_id', modelId)
  if (isTest != null) params.set('is_test', String(isTest))
  return apiJson(`/admin/purchases?${params}`, {}, 'Failed to load purchases')
}

export async function getModelFans(modelId, { search, limit = 50, offset = 0 } = {}) {
  return getFans({ modelId, search, limit, offset })
}

export async function getModelMessages(modelId, { limit = 50, offset = 0 } = {}) {
  return getMessages({ modelId, limit, offset })
}

export async function getModelPurchases(modelId, { limit = 50, offset = 0 } = {}) {
  return getPurchases({ modelId, limit, offset })
}

export async function getAccountConnectionEvents(modelId) {
  return apiJson(
    `/admin/models/${modelId}/account-connection-events`,
    {},
    'Failed to load connection history'
  );
}

export async function getChatConversations(modelId = null, isTest = false, { limit = 20, offset = 0 } = {}) {
  const params = new URLSearchParams({ limit, offset });
  if (modelId != null) params.set('model_id', modelId);
  params.set('is_test', String(isTest));
  return apiJson(
    `/admin/chats/conversations?${params}`,
    {},
    'Failed to load chats'
  );
}

export async function createTestChat(modelId, { username = 'test_fan', name, configId } = {}) {
  return apiJson(
    '/admin/chats/test-chat',
    {
      method: 'POST',
      body: JSON.stringify({ model_id: modelId, username, name, config_id: configId ?? null }),
    },
    'Failed to create test chat'
  );
}

export async function simulateIncomingMessage(modelId, fanId, text, media = null) {
  const params = new URLSearchParams({ model_id: modelId, fan_id: fanId });
  const body = { text };
  if (media && media.length > 0) body.media = media;
  return apiJson(
    `/admin/chats/conversation/simulate-message?${params}`,
    {
      method: 'POST',
      body: JSON.stringify(body),
    },
    'Failed to send message'
  );
}

export async function sendManualConversationMessage(modelId, fanId, text) {
  const params = new URLSearchParams({ model_id: modelId, fan_id: fanId });
  return apiJson(
    `/admin/chats/conversation/manual-message?${params}`,
    {
      method: 'POST',
      body: JSON.stringify({ text }),
    },
    'Failed to send manual message'
  );
}

export async function uploadTestMedia(modelId, fanId, file) {
  const params = new URLSearchParams({ model_id: modelId, fan_id: fanId });
  const formData = new FormData();
  formData.append('file', file);
  return apiJson(
    `/admin/chats/conversation/upload-test-media?${params}`,
    { method: 'POST', body: formData, skipContentType: true },
    'Failed to upload media'
  );
}

export async function simulatePurchase(modelId, fanId, { purchaseType = 'ppv_unlock', amount = 0, currency = 'USD', messageId } = {}) {
  const params = new URLSearchParams({ model_id: modelId, fan_id: fanId });
  return apiJson(
    `/admin/chats/conversation/simulate-purchase?${params}`,
    {
      method: 'POST',
      body: JSON.stringify({ purchase_type: purchaseType, amount, currency, message_id: messageId }),
    },
    'Failed to simulate purchase'
  );
}

export async function getChatConversation(modelId, fanId, { limit = 100, offset = 0 } = {}) {
  const safeLimit = Math.min(100, Math.max(1, Number(limit) || 100));
  const safeOffset = Math.max(0, Number(offset) || 0);
  const params = new URLSearchParams({
    model_id: modelId,
    fan_id: fanId,
    limit: safeLimit,
    offset: safeOffset,
  });
  return apiJson(
    `/admin/chats/conversation?${params}`,
    {},
    'Failed to load conversation'
  );
}

export async function updateChatConfig(modelId, fanId, configId) {
  const params = new URLSearchParams({ model_id: modelId, fan_id: fanId });
  return apiJson(
    `/admin/chats/conversation/config?${params}`,
    {
      method: 'PATCH',
      body: JSON.stringify({ config_id: configId ?? null }),
    },
    'Failed to update configuration'
  );
}

export async function updateTestChat(modelId, fanId, { username, name, configId } = {}) {
  const params = new URLSearchParams({ model_id: modelId, fan_id: fanId });
  const body = {};
  if (username !== undefined) body.username = username;
  if (name !== undefined) body.name = name;
  if (configId !== undefined) body.config_id = configId ?? null;
  return apiJson(
    `/admin/chats/conversation?${params}`,
    {
      method: 'PATCH',
      body: JSON.stringify(body),
    },
    'Failed to update test chat'
  );
}

export async function purgeChatConversation(modelId, fanId) {
  const params = new URLSearchParams({ model_id: modelId, fan_id: fanId })
  return apiJson(
    `/admin/chats/conversation?${params}`,
    {
      method: 'DELETE',
      body: JSON.stringify({ confirmed: true }),
    },
    'Failed to delete chat'
  )
}

export async function setChatManualMode(modelId, fanId, enabled, review = {}) {
  return apiJson(
    `/bot/models/${modelId}/fans/${fanId}/manual-mode`,
    {
      method: 'POST',
      body: JSON.stringify({ enabled: Boolean(enabled), ...review }),
      suppressErrorToast: !enabled,
    },
    'Failed to change chat mode'
  )
}

export async function setChatVipMode(modelId, fanId, enabled) {
  return apiJson(
    `/bot/models/${modelId}/fans/${fanId}/vip-mode`,
    {
      method: 'POST',
      body: JSON.stringify({ enabled: Boolean(enabled) }),
    },
    'Failed to change VIP mode'
  )
}

export async function setChatPpvPermission(modelId, fanId, enabled, expectedRevision) {
  const body = { enabled: Boolean(enabled) }
  if (Number.isInteger(expectedRevision) && expectedRevision >= 0) {
    body.expected_revision = expectedRevision
  }
  return apiJson(
    `/bot/models/${modelId}/fans/${fanId}/ppv-permission`,
    { method: 'POST', body: JSON.stringify(body) },
    'Failed to change PPV permission',
  )
}

export async function createVipSalesSignal(modelId, fanId, payload) {
  return apiJson(
    `/bot/models/${modelId}/fans/${fanId}/vip-sales-signal`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
    'Failed to create VIP sales signal'
  )
}

export async function getChatSafetyStatus(modelId, fanId) {
  return apiJson(`/bot/models/${modelId}/fans/${fanId}/safety-status`, { suppressErrorToast: true })
}

export async function unlockChatSafetyLock(modelId, fanId, token) {
  return apiJson(
    `/bot/models/${modelId}/fans/${fanId}/unlock`,
    {
      method: 'POST',
      body: token ? JSON.stringify({ expected_safety_lock_token: token }) : undefined,
    },
    'Failed to unlock'
  )
}

export async function getFanBotProfile(modelId, fanId) {
  return apiJson(
    `/bot/models/${modelId}/fans/${fanId}/profile`,
    { suppressErrorToast: true },
    'Failed to load fan bot profile'
  )
}

export async function getBotMessageLog(messageId) {
  return apiJson(
    `/bot/messages/${messageId}/log`,
    {},
    'Failed to load message log'
  )
}

export async function getBotLogsList({ modelId, fanId, status, initiatedBy, limit = 50, offset = 0 } = {}) {
  const params = new URLSearchParams({ limit, offset })
  if (modelId != null) params.set('model_id', modelId)
  if (fanId != null) params.set('fan_id', fanId)
  if (status) params.set('status', status)
  if (initiatedBy) params.set('initiated_by', initiatedBy)
  return apiJson(
    `/bot/logs?${params}`,
    {},
    'Failed to load logs'
  )
}

export async function getBotLogs(params = {}) {
  return getBotLogsList(params)
}

export async function getBotLogDetail(graphId) {
  return apiJson(
    `/bot/logs/${graphId}`,
    {},
    'Failed to load log'
  )
}

export async function getProactiveRuns({ limit = 50, offset = 0, hasAgentRuns = false } = {}) {
  const params = new URLSearchParams({ limit, offset })
  if (hasAgentRuns) params.set('has_agent_runs', 'true')
  return apiJson(
    `/bot/proactive-runs?${params}`,
    {},
    'Failed to load runs'
  )
}

export async function getProactiveRunAgentRuns(runId, { limit = 20, offset = 0 } = {}) {
  const params = new URLSearchParams({ limit, offset })
  return apiJson(
    `/bot/proactive-runs/${runId}/agent-runs?${params}`,
    {},
    'Failed to load agent runs'
  )
}

export async function getProactiveAgentRunDetail(agentRunId) {
  return apiJson(
    `/bot/proactive-agent-runs/${agentRunId}`,
    {},
    'Failed to load details'
  )
}

export async function getAnalyticsSnapshots({
  scopeType,
  scopeId,
  snapshotType,
  period,
  isTest,
  limit = 200,
} = {}) {
  const params = new URLSearchParams()
  if (scopeType) params.set('scope_type', scopeType)
  if (scopeId) params.set('scope_id', scopeId)
  if (snapshotType) params.set('snapshot_type', snapshotType)
  if (period) params.set('period', period)
  if (isTest != null) params.set('is_test', String(isTest))
  if (limit != null) params.set('limit', String(limit))
  return apiJson(
    `/bot/analytics/snapshots?${params}`,
    {},
    'Failed to load analytics'
  )
}

export async function recomputeAnalytics({ sync = false } = {}) {
  return apiJson(
    '/bot/analytics/recompute',
    {
      method: 'POST',
      body: JSON.stringify({ sync: Boolean(sync) }),
    },
    'Failed to recompute analytics'
  )
}

export async function exportMessageLog(messageId) {
  return apiJson(
    `/bot/messages/${messageId}/export`,
    {},
    'Failed to export message log'
  )
}

export async function exportChatHistory(modelId, fanId) {
  return apiJson(
    `/bot/chats/${modelId}/${fanId}/export`,
    {},
    'Failed to export chat history'
  )
}

export async function updateChatTelegramPeer(modelId, fanId, peer) {
  const params = new URLSearchParams({ model_id: modelId, fan_id: fanId })
  return apiJson(
    `/chats/conversation/telegram-peer?${params}`,
    {
      method: 'PATCH',
      body: JSON.stringify({ peer: peer || null }),
    },
    'Failed to update Telegram peer'
  )
}
